import { Fragment, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import type { Prescription, PrescriptionStatus } from '@/features/consultation/types';
import { usePatientPrescriptions, usePrescriptionDetail, useClosePrescription } from './usePatientPrescriptions';

const STATUS_VARIANT: Record<PrescriptionStatus, 'success' | 'neutral' | 'info' | 'danger'> = {
  active: 'success',
  draft: 'neutral',
  completed: 'info',
  cancelled: 'danger',
  'entered-in-error': 'danger',
};

function PrescriptionItemsPanel({ id }: { id: number }) {
  const detailQuery = usePrescriptionDetail(id);
  const items = detailQuery.data?.items ?? [];
  if (detailQuery.isLoading) return <div className="p-3 text-center text-xs text-ink-muted">Loading medicines…</div>;
  if (!items.length) return <div className="p-3 text-center text-xs text-ink-muted">No medicines on this prescription.</div>;
  return (
    <div className="p-3">
      <Table>
        <TableHeader>
          <TableRow>{['Drug', 'Dose', 'Frequency', 'Duration', 'Prescribed', 'Dispensed', 'Remaining'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell>
                <div className="font-semibold">{it.drug_name} {it.brand_name ? `(${it.brand_name})` : ''}</div>
                <div className="text-[11px] text-ink-muted">{it.strength}</div>
              </TableCell>
              <TableCell className="text-xs">{[it.dose_quantity, it.dose_unit].filter(Boolean).join(' ') || '—'}</TableCell>
              <TableCell className="text-xs">{it.frequency_text || it.frequency_code || '—'}</TableCell>
              <TableCell className="text-xs">{it.duration_value ? `${it.duration_value}${it.duration_unit ?? 'd'}` : '—'}</TableCell>
              <TableCell className="text-xs">{it.quantity ?? '—'}</TableCell>
              <TableCell className="text-xs">{it.dispensed_quantity ?? 0}</TableCell>
              <TableCell className="text-xs">{it.remaining_quantity ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Cross-visit prescription list + close-out action for one patient —
 * shared between the Pharmacy module's "Rx History" tab (RxHistoryTab.tsx,
 * which does its own patient search first) and Patient Records' own
 * Prescriptions tab (which already has the patient in context). Takes only
 * `uhid`; the host screen owns finding the patient.
 *
 * Built because GET /prescriptions?visit_id= (what PrescriptionBuilder.tsx
 * uses) only ever shows ONE visit's prescription — a patient with several
 * active prescriptions across past visits had no screen listing all of
 * them, and (before PATCH /:id/close existed) no way to close one out
 * short of reopening the exact consultation it was written in.
 */
export default function PatientPrescriptionHistory({ uhid }: { uhid: string }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const listQuery = usePatientPrescriptions(uhid);
  const closeMutation = useClosePrescription(uhid);
  const prescriptions = listQuery.data ?? [];

  const closeError = closeMutation.error instanceof ApiError ? closeMutation.error.message : null;

  const markCompleted = (id: number) => {
    closeMutation.mutate({ id, status: 'completed' });
  };
  const confirmCancel = (id: number) => {
    const reason = cancelReason.trim();
    if (!reason) return;
    closeMutation.mutate({ id, status: 'cancelled', reason }, { onSuccess: () => { setCancellingId(null); setCancelReason(''); } });
  };

  if (listQuery.isLoading) return <LoadingState label="Loading prescriptions…" />;
  if (listQuery.isError) {
    return (
      <ErrorState
        message={listQuery.error instanceof ApiError ? listQuery.error.message : 'Could not load this patient’s prescriptions'}
        onRetry={() => listQuery.refetch()}
      />
    );
  }
  if (!prescriptions.length) return <EmptyState label="No prescriptions on record for this patient" />;

  return (
    <Card>
      <div className="mb-3.5 text-sm font-semibold">Prescriptions — every visit</div>
      {closeError && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{closeError}</div>}
      {prescriptions.map((rx: Prescription) => {
        const isOpen = expandedId === rx.id;
        const isActive = rx.status === 'active';
        const isCancelling = cancellingId === rx.id;
        return (
          <div key={rx.id} className="mb-2.5 rounded-[var(--radius)] border border-line">
            <div
              className="flex cursor-pointer items-center justify-between gap-2.5 px-3 py-2.5"
              onClick={() => setExpandedId(isOpen ? null : rx.id)}
            >
              <div>
                <div className="text-[13px] font-semibold">
                  {rx.prescribed_at ? new Date(rx.prescribed_at).toLocaleString('en-IN') : new Date(rx.created_at).toLocaleString('en-IN')}
                </div>
                <div className="mt-0.5 text-[11px] text-ink-muted">
                  {rx.visit_code ? `${rx.visit_code} · ` : ''}
                  {rx.branch_name ? `${rx.branch_name} · ` : ''}
                  {rx.prescriber_name ? `Dr. ${rx.prescriber_name} · ` : ''}
                  {rx.item_count ?? 0} medicine{(rx.item_count ?? 0) === 1 ? '' : 's'}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant={STATUS_VARIANT[rx.status]}>{rx.status}</Badge>
                <span className="text-xs text-ink-muted">{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>
            {isOpen && (
              <Fragment>
                <div className="border-t border-line">
                  <PrescriptionItemsPanel id={rx.id} />
                </div>
                {isActive && (
                  <div className="border-t border-line bg-canvas p-2.5" onClick={(e) => e.stopPropagation()}>
                    {!isCancelling ? (
                      <div className="flex gap-2">
                        <Button size="sm" variant="success" loading={closeMutation.isPending} onClick={() => markCompleted(rx.id)}>
                          ✓ Mark completed
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => { setCancellingId(rx.id); setCancelReason(''); }}>
                          ✕ Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Input
                          className="flex-1"
                          placeholder="Reason for cancelling (required)"
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                        />
                        <Button size="sm" variant="danger" loading={closeMutation.isPending} disabled={!cancelReason.trim()} onClick={() => confirmCancel(rx.id)}>
                          Confirm cancel
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setCancellingId(null)}>Back</Button>
                      </div>
                    )}
                  </div>
                )}
              </Fragment>
            )}
          </div>
        );
      })}
    </Card>
  );
}
